import type { MouseEvent } from "react";
import { Link } from "react-router-dom";

import { ROOT, LOGIN, LANDING_PAGE } from "@/routes";
import { appStoreUrl, playStoreUrl, type StoreLinkSurface } from "@/lib/storeLinks";
import { trackMarketingCtaClick } from "@/services/marketingCtaAnalytics.service";
import { CONTROL } from "@/lib/typography";
import { cn } from "@/lib/utils";

interface NavLink {
  label: string;
  href: string;
}

interface LoggedOutHeaderProps {
  logoLink?: string;
  navLinks?: NavLink[];
  loginHref?: string;
  /**
   * Page hosting the header; drives store-badge href attribution and the
   * click events' surface param. Defaults to the placement-level "header"
   * token for pages without a surface of their own.
   */
  surface?: StoreLinkSurface;
  onAppStoreClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  trackCtaClicks?: boolean;
}

const DEFAULT_NAV_LINKS: NavLink[] = [{ label: "FAQs", href: `${LANDING_PAGE()}#faq` }];

export const LoggedOutHeader = ({
  logoLink = ROOT(),
  navLinks = DEFAULT_NAV_LINKS,
  loginHref,
  surface = "header",
  onAppStoreClick,
  trackCtaClicks = true,
}: LoggedOutHeaderProps) => {
  const resolvedLoginHref = loginHref || LOGIN();
  const appStoreHref = appStoreUrl(surface);
  const playStoreHref = playStoreUrl(surface);

  const trackHeaderSignupLoginClick = () => {
    if (!trackCtaClicks) return;
    trackMarketingCtaClick({
      destination: "signup_login",
      surface,
      placement: "header",
      label: "Signup Free or Login",
      href: resolvedLoginHref,
    });
  };

  const trackHeaderAppStoreClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (trackCtaClicks) {
      trackMarketingCtaClick({
        destination: "app_store",
        surface,
        placement: "header",
        label: "Download on the App Store",
        href: appStoreHref,
      });
    }
    onAppStoreClick?.(e);
  };

  const trackHeaderPlayStoreClick = () => {
    if (!trackCtaClicks) return;
    trackMarketingCtaClick({
      destination: "play_store",
      surface,
      placement: "header",
      label: "Get it on Google Play",
      href: playStoreHref,
    });
  };

  return (
    <div className="relative w-full border-b border-foreground/10 bg-background">
      <div className="page-frame">
          <div className="flex items-center justify-between gap-4 py-3 md:py-5">
            <Link to={logoLink} className="flex items-center gap-3 shrink-0">
              {/* Mobile: typographic logo */}
              <img
                alt="Stasht logo"
                src="/Stasht - Typographic - orange.png"
                className="h-8 w-auto md:hidden dark:hidden"
              />
              <img
                alt="Stasht logo"
                src="/Stasht - Typographic - white.png"
                className="h-8 w-auto hidden dark:block md:!hidden"
              />
              {/* Desktop: full horizontal logo */}
              <img
                alt="Stasht logo"
                src="/Stasht full Horrizontal-orange.png"
                className="h-12 w-auto hidden md:block dark:md:hidden"
              />
              <img
                alt="Stasht logo"
                src="/Stasht Horrizontal-white.png"
                className="h-12 w-auto hidden dark:md:block"
              />
            </Link>

            <div className="hidden items-center gap-8 md:flex">
              <nav className="flex items-center gap-5 lg:gap-6">
                {navLinks.map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    className={cn(CONTROL, "text-foreground")}
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
              <Link
                to={resolvedLoginHref}
                className={cn(CONTROL, "rounded-full border border-foreground bg-foreground px-5 py-2 text-background")}
                onClick={trackHeaderSignupLoginClick}
              >
                Signup Free or Login
              </Link>
            </div>

            {/* Mobile Navigation - App Store badges */}
            <div className="flex items-center gap-2 md:hidden overflow-visible">
              <a
                href={appStoreHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackHeaderAppStoreClick}
              >
                <img alt="Download on the App Store" src="/Download_on_App_Store.svg" className="h-8 w-auto" />
              </a>
              <a
                href={playStoreHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackHeaderPlayStoreClick}
              >
                <img alt="Get it on Google Play" src="/Google_Play_Store_badge_EN.svg" className="h-8 w-auto" />
              </a>
            </div>
          </div>
      </div>
    </div>
  );
};
